/**
 * Wave 3 — PayBillModal (Feb 2026).
 *
 * Reusable atomic Pay-Bill modal. Renders on top of any bill row and
 * POSTs to /api/bills/{id}/pay which creates Payment + Bank
 * Transaction + Journal in one shot.
 *
 * Saudi-market payment methods are first-class: SADAD shows three
 * dedicated fields (biller_no, bill_no, reference_no), CHECK shows
 * check_no, BANK_TRANSFER shows a reference field. Bank fees + their
 * VAT can be added inline so the bank-charge journal lines are
 * captured on the same posting (matches the real ZATCA behaviour).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X, CreditCard, Banknote, FileText, Loader2 } from 'lucide-react';
import { axiosInstance } from '../App';

const METHOD_LABELS = {
  cash:          'نَقد (Cash)',
  bank_transfer: 'تَحويل بَنكي (Bank Transfer)',
  check:         'شيك (Check)',
  credit_card:   'بِطاقة ائتمان (Credit Card)',
  sadad:         'سداد (SADAD)',
  stc_pay:       'STC Pay',
  apple_pay:     'Apple Pay',
  mada:          'مدى (Mada)',
  other:         'أُخرى (Other)',
};

const PayBillModal = ({ bill, onClose, onPaid }) => {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    bank_account_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    amount: bill?.amount_remaining || 0,
    reference_no: '',
    sadad_biller_no: bill?.biller_no || '',
    sadad_bill_no: bill?.sadad_bill_no || '',
    sadad_reference_no: '',
    check_no: '',
    bank_fee_amount: 0,
    bank_fee_vat_amount: 0,
    bank_fee_has_invoice: false,
    transfer_notification_url: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get('/bank/accounts');
        const active = (data || []).filter((a) => a.status === 'active');
        setAccounts(active);
        if (active.length === 1) {
          setForm((f) => ({ ...f, bank_account_id: active[0].id }));
        }
      } catch (e) {
        toast.error('تعذَّر تحميل الحسابات البنكية');
      }
    })();
  }, []);

  const isPartial = useMemo(
    () => Number(form.amount) > 0 && Number(form.amount) < (bill?.amount_remaining || 0),
    [form.amount, bill?.amount_remaining],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bank_account_id) {
      toast.error('اختَر الحِساب البَنكي');
      return;
    }
    if (Number(form.amount) <= 0) {
      toast.error('المَبلَغ يَجِب أن يَكون أكبر من صِفر');
      return;
    }
    if (form.payment_method === 'sadad' && !form.sadad_biller_no) {
      toast.error('سداد يَتَطَلَّب رَقم المُفوتر (Biller Number)');
      return;
    }
    if (form.payment_method === 'check' && !form.check_no) {
      toast.error('الشيك يَتَطَلَّب رَقم الشيك');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        bank_account_id: form.bank_account_id,
        payment_date: form.payment_date,
        payment_method: form.payment_method,
        amount: parseFloat(form.amount),
        reference_no: form.reference_no || null,
        sadad_biller_no: form.payment_method === 'sadad' ? form.sadad_biller_no : null,
        sadad_bill_no: form.payment_method === 'sadad' ? form.sadad_bill_no : null,
        sadad_reference_no: form.payment_method === 'sadad' ? form.sadad_reference_no : null,
        check_no: form.payment_method === 'check' ? form.check_no : null,
        bank_fee_amount: parseFloat(form.bank_fee_amount) || 0,
        bank_fee_vat_amount: parseFloat(form.bank_fee_vat_amount) || 0,
        bank_fee_has_invoice: !!form.bank_fee_has_invoice,
        transfer_notification_url: form.transfer_notification_url || null,
        notes: form.notes || null,
      };
      const { data } = await axiosInstance.post(`/bills/${bill.id}/pay`, payload);
      toast.success(
        `تم السَّداد ✓ ${data.payment_no} · ${data.bank_transaction_no} · J-${data.journal_id?.slice(0, 8)}`,
      );
      onPaid?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فَشِل السَّداد');
    } finally {
      setSaving(false);
    }
  };

  const showSadad = form.payment_method === 'sadad';
  const showCheck = form.payment_method === 'check';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="pay-bill-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-auto">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center text-white">
              <CreditCard className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">سَداد الفاتورة</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {bill?.bill_number} · {bill?.supplier_name || bill?.biller_no || '—'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-pay-bill-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 grid grid-cols-3 gap-2 text-sm">
          <div className="bg-blue-50 rounded-lg p-2.5">
            <div className="text-[11px] text-blue-600">الإجمالي</div>
            <div className="font-bold text-blue-800">{(bill?.total || 0).toLocaleString()}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-2.5">
            <div className="text-[11px] text-green-600">المَدفوع</div>
            <div className="font-bold text-green-800">{(bill?.amount_paid || 0).toLocaleString()}</div>
          </div>
          <div className="bg-red-50 rounded-lg p-2.5">
            <div className="text-[11px] text-red-600">المُتَبَقي</div>
            <div className="font-bold text-red-800">{(bill?.amount_remaining || 0).toLocaleString()}</div>
          </div>
        </div>

        <form onSubmit={submit} className="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-gray-700">الحِساب البَنكي *</span>
            <select
              required
              data-testid="pay-bill-bank-account"
              value={form.bank_account_id}
              onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            >
              <option value="">— اختَر —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.bank_name} — {String(a.account_number).slice(-4)} · {a.currency} · {(a.current_balance || 0).toLocaleString()}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-gray-700">تاريخ السَّداد *</span>
            <input
              type="date" required
              data-testid="pay-bill-date"
              value={form.payment_date}
              onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>

          <label className="block">
            <span className="text-gray-700">طَريقة الدَّفع *</span>
            <select
              required
              data-testid="pay-bill-method"
              value={form.payment_method}
              onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            >
              {Object.keys(METHOD_LABELS).map((m) => (
                <option key={m} value={m}>{METHOD_LABELS[m]}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-gray-700">المَبلَغ *</span>
            <input
              type="number" step="0.01" min="0.01" required
              max={bill?.amount_remaining}
              data-testid="pay-bill-amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
            {isPartial && (
              <span className="text-[11px] text-amber-600 mt-1 block">
                ⚠ سَداد جُزئي — المُتَبَقي بَعد العَمَلِيَّة:{' '}
                {((bill?.amount_remaining || 0) - Number(form.amount)).toLocaleString()} ريال
              </span>
            )}
          </label>

          <label className="block md:col-span-2">
            <span className="text-gray-700">رَقم العَمَلِيَّة / المَرجِع</span>
            <input
              type="text"
              data-testid="pay-bill-reference"
              value={form.reference_no}
              onChange={(e) => setForm({ ...form, reference_no: e.target.value })}
              placeholder="TRX-2026-…"
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>

          {showSadad && (
            <>
              <label className="block">
                <span className="text-gray-700">رَقم المُفوتر (Biller) *</span>
                <input
                  type="text" required
                  data-testid="pay-bill-sadad-biller"
                  value={form.sadad_biller_no}
                  onChange={(e) => setForm({ ...form, sadad_biller_no: e.target.value })}
                  placeholder="STC: 900، كهرباء: 100…"
                  className="mt-1 w-full px-3 py-2 border rounded-lg"
                />
              </label>
              <label className="block">
                <span className="text-gray-700">رَقم فاتورة سداد</span>
                <input
                  type="text"
                  data-testid="pay-bill-sadad-bill"
                  value={form.sadad_bill_no}
                  onChange={(e) => setForm({ ...form, sadad_bill_no: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border rounded-lg"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="text-gray-700">SADAD Reference</span>
                <input
                  type="text"
                  data-testid="pay-bill-sadad-ref"
                  value={form.sadad_reference_no}
                  onChange={(e) => setForm({ ...form, sadad_reference_no: e.target.value })}
                  placeholder="رَقم مَرجِع سَداد (من إيصال السَّداد)"
                  className="mt-1 w-full px-3 py-2 border rounded-lg"
                />
              </label>
            </>
          )}

          {showCheck && (
            <label className="block md:col-span-2">
              <span className="text-gray-700">رَقم الشيك *</span>
              <input
                type="text" required
                data-testid="pay-bill-check-no"
                value={form.check_no}
                onChange={(e) => setForm({ ...form, check_no: e.target.value })}
                className="mt-1 w-full px-3 py-2 border rounded-lg"
              />
            </label>
          )}

          {/* Bank fees block */}
          <div className="md:col-span-2 border rounded-lg p-3 bg-amber-50/30">
            <div className="flex items-center gap-2 mb-2 text-amber-700 text-xs font-medium">
              <Banknote className="w-3.5 h-3.5" />
              رُسوم البَنك (إن وُجِدَت)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <label className="block">
                <span className="text-gray-700 text-xs">مَبلَغ الرَّسم</span>
                <input
                  type="number" step="0.01" min="0"
                  data-testid="pay-bill-fee"
                  value={form.bank_fee_amount}
                  onChange={(e) => setForm({ ...form, bank_fee_amount: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border rounded-lg"
                />
              </label>
              <label className="block">
                <span className="text-gray-700 text-xs">VAT على الرَّسم</span>
                <input
                  type="number" step="0.01" min="0"
                  data-testid="pay-bill-fee-vat"
                  value={form.bank_fee_vat_amount}
                  onChange={(e) => setForm({ ...form, bank_fee_vat_amount: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border rounded-lg"
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700 mt-5">
                <input
                  type="checkbox"
                  data-testid="pay-bill-fee-has-invoice"
                  checked={form.bank_fee_has_invoice}
                  onChange={(e) => setForm({ ...form, bank_fee_has_invoice: e.target.checked })}
                />
                توجد فاتورة لِلرَّسم؟
              </label>
            </div>
          </div>

          <label className="block md:col-span-2">
            <span className="text-gray-700">رابِط إشعار التَّحويل (اختياري)</span>
            <input
              type="url"
              data-testid="pay-bill-notification-url"
              value={form.transfer_notification_url}
              onChange={(e) => setForm({ ...form, transfer_notification_url: e.target.value })}
              placeholder="https://..."
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>

          <label className="block md:col-span-2">
            <span className="text-gray-700">مُلاحَظات</span>
            <textarea
              rows={2}
              data-testid="pay-bill-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>

          <div className="md:col-span-2 flex justify-end gap-2 pt-3 border-t">
            <button
              type="button" onClick={onClose}
              className="px-4 py-2 text-sm border rounded-lg"
              data-testid="cancel-pay-bill"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="submit-pay-bill"
              className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {saving ? 'جارٍ التَّسجيل...' : 'تَأكيد السَّداد وإصدار القَيد'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayBillModal;
